// RichTextEditorOneFile.jsx
import React, {
  memo,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  useState,
  useId,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { LuSparkles } from "react-icons/lu";
import svgImg from "../../../../Assets/LoadingScreen/bgsvg.gif";
import useCloudinaryMediaUpload from "../../../../Hooks/CommonHooks/useCloudinaryMediaUpload";
import useCloudinaryDeleteByPublicId from "../../../../Hooks/CommonHooks/useCloudinaryDeleteByPublicId";
import { FaFileUpload, FaImage } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { PiMagicWand, PiPaintBrushHousehold } from "react-icons/pi";
import { IoReorderFourOutline, IoSearchOutline } from "react-icons/io5";
import { BsArrowsCollapseVertical } from "react-icons/bs";

/** ----------------------------
 * Helpers (inline, single file)
 * --------------------------- */
function createImageWrapper(url /*, publicId*/) {
  return `<img src="${url}" alt="Uploaded" />`;
}

function createFileWrapper(url /*, publicId*/) {
  return `
    <a href="${url}" target="_blank" rel="noopener noreferrer"
       style="padding:6px 12px;background:#C71585;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;">📄 View PDF</a>
  `;
}

function makeProgressBar(hostEl) {
  if (!hostEl) return {};
  const container = document.createElement("div");
  Object.assign(container.style, {
    position: "relative",
    width: "100%",
    height: "5px",
    background: "#f3f4f6",
    marginTop: "6px",
    borderRadius: "4px",
    overflow: "hidden",
    top: "20%",
  });

  const bar = document.createElement("div");
  Object.assign(bar.style, {
    width: "0%",
    height: "100%",
    background: "#C71585",
    transition: "width 0.2s ease",
  });

  container.appendChild(bar);
  hostEl.appendChild(container);
  return { bar, container };
}

function setProgress(pui, pct) {
  if (pui?.bar) pui.bar.style.width = `${Math.min(Math.max(pct, 0), 100)}%`;
}

function removeProgressBar(pui) {
  if (pui?.container?.parentNode)
    pui.container.parentNode.removeChild(pui.container);
}

/** ----------------------------
 * Main Component
 * --------------------------- */
const RichTextEditorOneFile = ({
  assignmentLabel = "Title",
  hideInput = false,
  editorHeight,
  assignmentName = "",
  editorContent,
  onNameChange = () => {},
  onEditorChange,
  inputPlaceHolder = "Enter title…",
  nameError,
  contentError,
  inputRef,
  readOnly = false,
  isCreateQuestion = false, // parity-only flag
  isStudentApplication = false, // parity-only flag
  priority,
  endDate,
  startDate,
  title,
  systemPrompt,
  toolbarId: toolbarIdProp, // NEW
}) => {
  const quillRef = useRef(null);
  const processedDeletionsRef = useRef(new Set());
  const internalId = useId().replace(/:/g, "");
  const toolbarId = toolbarIdProp || `custom-toolbar-${internalId}`;

  const { uploadImage, uploadFile } = useCloudinaryMediaUpload();
  const { deleteMediaByPublicId } = useCloudinaryDeleteByPublicId();

  /** Upload (image / pdf) with progress and insert wrapped HTML */
  const uploadWithProgress = useCallback(
    async (file, kind, wrapToHtml) => {
      const editor = quillRef.current?.getEditor?.();
      if (!file || !editor) return;

      // Progress bar anchored to this editor's toolbar
      const toolbarRoot =
        document.getElementById(toolbarId) ||
        quillRef.current?.editor?.container?.parentNode;
      const pui = makeProgressBar(toolbarRoot);

      try {
        const uploader = kind === "image" ? uploadImage : uploadFile;
        const res = await uploader(
          file,
          (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded * 100) / e.total);
              setProgress(pui, pct);
            }
          },
          "editor-contents"
        );

        if (res?.secure_url /* && res?.public_id */) {
          const html = wrapToHtml(res.secure_url, res.public_id);
          const range = editor.getSelection(true);
          const index = range ? range.index : editor.getLength();
          editor.clipboard.dangerouslyPasteHTML(index, html);
          toast.success(kind === "image" ? "Image uploaded" : "PDF uploaded");
        } else {
          toast.error("Upload failed: Missing URL/public_id.");
        }
      } catch (err) {
        toast.error("Upload failed. Please try again.");
      } finally {
        setProgress(pui, 100);
        setTimeout(() => removeProgressBar(pui), 300);
      }
    },
    [toolbarId, uploadImage, uploadFile]
  );

  const handleImageUpload = useCallback(() => {
    const el = document.createElement("input");
    el.type = "file";
    el.accept = "image/*";
    el.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/"))
        return toast.error("Invalid image type");
      uploadWithProgress(file, "image", createImageWrapper);
    };
    el.click();
  }, [uploadWithProgress]);

  const handleFileUpload = useCallback(() => {
    const el = document.createElement("input");
    el.type = "file";
    el.accept = "application/pdf";
    el.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.type !== "application/pdf")
        return toast.error("Only PDF allowed");
      uploadWithProgress(file, "file", createFileWrapper);
    };
    el.click();
  }, [uploadWithProgress]);

  const [showAi, setShowAi] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const openAiInput = useCallback(() => {
    setShowAi((s) => !s);
  }, []);

  /** Delete handler for wrapped assets (scoped to this editor) */
  const onEditorClick = useCallback(
    async (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.classList.contains("delete-btn")) return;

      e.preventDefault();
      e.stopPropagation();

      const wrapper =
        target.closest(".uploaded-image-wrapper") ||
        target.closest(".uploaded-file-wrapper");
      if (!wrapper) return;

      const publicId = wrapper.getAttribute("data-public-id") || "";
      if (!publicId || processedDeletionsRef.current.has(publicId)) return;

      processedDeletionsRef.current.add(publicId);

      const toolbarRoot =
        document.getElementById(toolbarId) ||
        quillRef.current?.editor?.container?.parentNode;
      const pui = makeProgressBar(toolbarRoot);

      try {
        const data = await deleteMediaByPublicId(publicId);
        setProgress(pui, 100);
        setTimeout(() => removeProgressBar(pui), 300);

        wrapper.remove();
        const editor = quillRef.current?.getEditor?.();
        if (editor) onEditorChange?.(editor.root.innerHTML);

        toast.success(
          data?.result === "ok"
            ? "Deleted successfully"
            : "Removed locally (not found on Cloudinary)"
        );
      } catch {
        setProgress(pui, 100);
        setTimeout(() => removeProgressBar(pui), 300);
        processedDeletionsRef.current.delete(publicId);
        toast.error("Delete failed");
      }
    },
    [deleteMediaByPublicId, onEditorChange, toolbarId]
  );

  /** Bind click for delete buttons inside THIS editor only */
  useEffect(() => {
    const editorRoot = quillRef.current?.getEditor?.()?.root;
    if (!editorRoot) return;
    editorRoot.addEventListener("click", onEditorClick);
    return () => editorRoot.removeEventListener("click", onEditorClick);
  }, [onEditorClick]);

  /** Quill modules with custom handlers to mirror Jodit */
  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`, // scoped toolbar
        handlers: {
          imageUpload: handleImageUpload,
          fileUpload: handleFileUpload,
          ai: openAiInput,
        },
      },
      clipboard: { matchVisual: false },
      history: { delay: 500, maxStack: 200, userOnly: true },
    }),
    [toolbarId, handleImageUpload, handleFileUpload, openAiInput]
  );

  const genrateContent = async (e, defaultPrompt) => {
    if (e?.stopPropagation) e.stopPropagation();
    setAiLoading(true);

    onEditorChange?.(
      `<div style="text-align: center;">
        <img alt="AI is thinking..." src="${svgImg}" />
      </div>`
    );

    try {
      const res = await fetch(
        "https://chatbot.studentdiwan.com:5678/webhook/33334935-8a06-4565-8ee5-dabdcfc1bc93/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: "7ab3a2c30052489ead9ca3b9c27a1db4",
            action: "sendMessage",
            chatInput: defaultPrompt || userPrompt,
            systemPrompt: `
${systemPrompt || "You are a Flow AI"}
${
  editorContent
    ? `*DO NOT REPLY ACCORDING YOUR LAST RESPONSE. user want reponse according this current chapter  content : "${editorContent}"`
    : ""
}
${
  title
    ? `Notice deatils  title:${title},priority:${priority},startDate:${startDate}, endDate:${endDate}`
    : ""
}
You are Flow AI, a system that generates only HTML output for use in rich text editors (e.g., React Quill).

✅ Rules:
HTML Only: Output strictly valid HTML. No greetings, no markdown.
Inline CSS only. Use rgb() colors.
Use semantic tags. Default font-family: "Arial, sans-serif".
Never return explanations or code fences.
            `,
          }),
        }
      );

      const data = await res.json();
      if (data?.output) {
        const out = data.output.startsWith("```html")
          ? data.output.slice(7, data.output.length - 3)
          : data.output;
        onEditorChange?.(out);
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="w-full bg-white p-2 relative">
      {showAi ? (
        <div className="flex flex-col w-full max-w-[60rem] h-auto py-4 absolute bottom-0 z-[9999] bg-gradient-to-b from-[#FFE6ED] to-[#DDBCFF] p-4 border border-gray-400">
          <div className="flex items-center relative ">
            <input
              type="text"
              placeholder={"Type your prompt here..."}
              onChange={(e) => setUserPrompt(e.target.value)}
              className="w-full px-2 h-[2.7rem] border-white rounded-lg"
            />
            <button
              onClick={genrateContent}
              disabled={aiLoading}
              className="absolute right-2 flex items-center justify-center bg-gradient-to-r from-[#C83B62] to-[#7F35CD] text-white rounded-lg pl-1 shadow transition-transform duration-300 hover:scale-105 p-1"
            >
              <IoMdSend color="white" size={22} />
            </button>
          </div>

          <div className="flex flex-row flex-wrap items-center gap-4 mt-4">
            <select
              id="language-select"
              className="w-[8rem] h-[1.5rem] rounded-lg bg-[#f6ebfc]"
              onChange={(e) =>
                genrateContent(e, `Translate into ${e.target.value}`)
              }
              defaultValue=" "
            >
              <option value=" ">
                <span className="text-purple-500">🌐</span> Translate
              </option>
              {[
                "English",
                "Arabic",
                "Russian",
                "Urdu",
                "Hindi",
                "Indonesian",
              ].map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>

            {[
              {
                icon: <PiMagicWand color="purple" />,
                title: "Improve Writing",
                prompt: "Improve the writing",
              },
              {
                icon: <PiPaintBrushHousehold color="purple" />,
                title: "Simple Writing",
                prompt: "Simplify the text",
              },
              {
                icon: <IoReorderFourOutline color="purple" />,
                title: "Elaborate",
                prompt: "Elaborate on the text",
              },
              {
                icon: <BsArrowsCollapseVertical color="purple" />,
                title: "Shorten",
                prompt: "Shorten the text",
              },
              {
                icon: <IoSearchOutline color="purple" />,
                title: "Fix Spelling & Grammar",
                prompt: "Fix spelling and grammar",
              },
            ].map((item) => (
              <button
                key={item.title}
                onClick={(e) => genrateContent(e, item.prompt)}
                className="flex flex-row gap-2 h-[1.5rem] items-center justify-center rounded-lg bg-[#f6ebfc] px-3"
              >
                {item.icon} {item.title}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {!hideInput && (
        <div className="mb-2">
          <label className="text-gray-500">{assignmentLabel}</label>

          {!isStudentApplication && (
            <>
              <input
                type="text"
                placeholder={inputPlaceHolder}
                value={assignmentName}
                onChange={(e) => onNameChange(e.target.value)}
                ref={inputRef}
                className={`w-full p-2 border rounded-sm ${
                  nameError ? "border-red-500" : "border-gray-300"
                }`}
                spellCheck
              />
              {nameError && (
                <p className="text-red-500 text-sm mt-1">{nameError}</p>
              )}
            </>
          )}
        </div>
      )}

      <div
        id={toolbarId}
        className="flex items-center justify-between w-full max-w-[60rem]"
      >
        <div className="w-[96%] flex flex-wrap items-center gap-1">
          <select className="ql-font" aria-label="Font family"></select>

          <select className="ql-header" aria-label="Heading">
            <option value="1" />
            <option value="2" />
            <option value="3" />
            <option value="" />
          </select>

          <button className="ql-bold" aria-label="Bold"></button>
          <button className="ql-italic" aria-label="Italic"></button>
          <button className="ql-underline" aria-label="Underline"></button>
          <button className="ql-strike" aria-label="Strikethrough"></button>

          <select className="ql-color" aria-label="Text color"></select>
          <select
            className="ql-background"
            aria-label="Background color"
          ></select>

          <button
            className="ql-script"
            value="sub"
            aria-label="Subscript"
          ></button>
          <button
            className="ql-script"
            value="super"
            aria-label="Superscript"
          ></button>

          <button
            className="ql-list"
            value="ordered"
            aria-label="Ordered list"
          ></button>
          <button
            className="ql-list"
            value="bullet"
            aria-label="Bullet list"
          ></button>

          <button
            className="ql-align"
            value="center"
            aria-label="Align center"
          ></button>
          <button
            className="ql-align"
            value="right"
            aria-label="Align right"
          ></button>
          <button
            className="ql-align"
            value="justify"
            aria-label="Justify"
          ></button>

          <button className="ql-blockquote" aria-label="Blockquote"></button>
          <button className="ql-code-block" aria-label="Code block"></button>

          <button
            className="ql-direction"
            value="rtl"
            aria-label="Right to Left"
          ></button>

          <button className="ql-link" aria-label="Link"></button>

          <button className="ql-imageUpload" aria-label="Upload image">
            <FaImage />
          </button>
          <button className="ql-fileUpload" aria-label="Upload file">
            <FaFileUpload />
          </button>
        </div>

        <div className="w-[4%] bg-gradient-to-b from-[#ebe4f2] to-[#e1c8f7] rounded-full flex items-center justify-center py-1">
          <button className="ql-ai" aria-label="AI Assistant">
            <LuSparkles color="purple" size={18} />
          </button>
        </div>
      </div>
<div
    className="relative"
    style={{ paddingBottom: showAi ? "20rem" + 12 : 0 }}
  >
      <ReactQuill
        ref={quillRef}
        value={editorContent}
        onChange={onEditorChange}
        readOnly={readOnly}
        modules={modules}
        theme="snow"
        style={{ minHeight: isCreateQuestion ? 300 : editorHeight || 400 }}
        className="h-[30rem] w-full max-w-[60rem] overflow-y-auto overflow-x-auto"
      />
      {showAi ? <div className="h-[7rem]" /> : null}
</div>
      {contentError && (
        <p className="text-red-500 text-sm mt-1">{contentError}</p>
      )}
    </div>
  );
};

export default memo(RichTextEditorOneFile);
